import { z } from 'zod';
import { Id, Timestamp } from './primitives.js';
/**
 * FR-16: the five signals, collected automatically from the audit trail
 * WITHOUT blocking the user's flow. Evaluated after check-out (TAD 4.2 `fraud`).
 *
 * The flag-rather-than-block approach is deliberate and is the correct design
 * per TAD RSK-07 — the SPK's stricter wording is to be aligned to the FRD, not
 * the other way round.
 */
export const FraudSignalCode = z.enum([
    'GPS_OUT_OF_TOLERANCE', //     1. check-in/out beyond the outlet radius
    'EXIF_TIMESTAMP_ANOMALY', //   2. photo EXIF far from check-in time (default > 1h, configurable)
    'MISSING_EXIF_METADATA', //    3. photo carries no EXIF, or EXIF with no capture time
    'EXTREME_VISIT_DURATION', //   4. < 2 min or > 4 h
    'REPEATED_LOCATION_PATTERN', //5. many consecutive visits from the same GPS point
]);
export const FRAUD_SIGNAL_CODES = FraudSignalCode.options;
/**
 * The documented FR-16 baseline — what a fresh database starts with.
 *
 * NO LONGER WHAT THE EVALUATOR READS. Since DAT-04 the running values live in
 * `app_settings` and are read through `SettingsService`; these are the migration
 * defaults — the CURRENT column defaults are exactly these values — and the
 * numbers the FRD quotes. Keeping both
 * is only safe because they cannot drift silently: there is no code path left
 * that falls back to these when the settings row is missing — a database without
 * that row raises rather than quietly evaluating against a constant.
 *
 * "Current", not "0032's", since 0035: `repeatVisitCount` moved from 3 to 10
 * when the one-visit-per-outlet-per-month rule became a warning and legitimate
 * return trips started happening. An applied migration is a record of what was
 * true when it ran, so the value to match is the live column default, not the
 * one the column was born with.
 *
 * The last three were never here at all before DAT-04; they were inline in the
 * evaluator's repeat-location query.
 */
export const FRAUD_DEFAULTS = Object.freeze({
    exifTimestampSkewSeconds: 60 * 60,
    minVisitDurationSeconds: 2 * 60,
    maxVisitDurationSeconds: 4 * 60 * 60,
    repeatVisitCount: 10,
    repeatWindowDays: 7,
    repeatRadiusM: 50,
    slaWarnDays: 3,
    slaDangerDays: 7,
});
/**
 * The running FR-16 configuration — FR-16 "thresholds are configurable", DAT-04.
 *
 * THE BOUNDS ARE NOT DECORATION. Each one rules out a value that would disable a
 * signal rather than tune it, and a disabled signal is indistinguishable on
 * screen from one that simply never fires — the failure mode that would take
 * months to notice. The two cross-field rules (`min < max`, `warn <= danger`)
 * are enforced here AND as a CHECK in 0032, because this row is written by
 * migrations and by psql sessions that never pass through zod.
 */
export const FraudSettings = z
    .object({
    exifTimestampSkewSeconds: z.number().int().min(60).max(86_400),
    minVisitDurationSeconds: z.number().int().min(1).max(3_600),
    maxVisitDurationSeconds: z.number().int().min(3_600).max(86_400),
    repeatVisitCount: z.number().int().min(2).max(50),
    repeatWindowDays: z.number().int().min(1).max(90),
    repeatRadiusM: z.number().int().min(5).max(5_000),
    slaWarnDays: z.number().int().min(1).max(365),
    slaDangerDays: z.number().int().min(1).max(365),
})
    .refine((value) => value.minVisitDurationSeconds < value.maxVisitDurationSeconds, {
    message: 'durasi minimum harus lebih kecil dari durasi maksimum',
    path: ['minVisitDurationSeconds'],
})
    .refine((value) => value.slaWarnDays <= value.slaDangerDays, {
    message: 'batas peringatan tidak boleh melebihi batas terlambat',
    path: ['slaWarnDays'],
});
/** `PUT /fraud/settings`. Every field is required — a partial write of a threshold set is how half a configuration goes live. */
export const UpdateFraudSettingsRequest = FraudSettings;
/** `GET /fraud/settings` — the values, plus who last touched them. */
export const FraudSettingsView = z.object({
    settings: FraudSettings,
    updatedAt: Timestamp.nullable(),
    updatedByUserName: z.string().nullable(),
});
export const FraudSignal = z.object({
    code: FraudSignalCode,
    /** Evidence rendered for the supervisor, e.g. "412 m from outlet (tolerance 100 m)". */
    detail: z.string().max(500),
    evaluatedAt: Timestamp,
});
/** FR-16 supervisor outcomes, feeding HR/operational evaluation. */
export const FlagDisposition = z.enum(['PENDING', 'APPROVED', 'FOLLOW_UP', 'REJECTED']);
/**
 * The two states that still need a human — the supervisor's actual queue.
 *
 * `PENDING` is what the worker writes when it raises a flag; `FOLLOW_UP` is what
 * a supervisor chooses to say "picked this up, not finished". Both are OPEN.
 * The distinction that matters operationally is open-vs-resolved, not
 * touched-vs-untouched, which is why this list exists rather than each caller
 * spelling out the pair and one of them eventually forgetting `FOLLOW_UP`.
 */
export const OPEN_DISPOSITIONS = ['PENDING', 'FOLLOW_UP'];
export const FlaggedVisit = z.object({
    id: Id,
    visitId: Id,
    outletId: Id,
    outletName: z.string(),
    salesUserId: Id,
    salesUserName: z.string(),
    visitDate: Timestamp,
    signals: z.array(FraudSignal),
    disposition: FlagDisposition,
    dispositionNote: z.string().max(2000).nullable(),
    dispositionByUserId: Id.nullable(),
    dispositionAt: Timestamp.nullable(),
    /** Set only while FOLLOW_UP — when this case is expected to be resolved. */
    dueAt: Timestamp.nullable(),
    /** When the salesperson was asked about this flag, off-system (TAD 4.6.1). */
    staffContactedAt: Timestamp.nullable(),
    /**
     * Days this flag has been waiting on a human, derived server-side: since it
     * was raised while PENDING, since it was picked up once FOLLOW_UP, and null
     * once resolved. Computed rather than stored — see the column comment.
     */
    agingDays: z.number().int().nullable(),
});
/**
 * The minimum number of RESOLVED flags before a rate is worth printing.
 *
 * Shared by the API and the console so the two cannot disagree about when a
 * percentage becomes meaningful. During a two-week trial with a small team, a
 * signal with six decided flags supports no conclusion at all, and a
 * confident-looking "83%" drawn from five observations is worse than a blank —
 * it is the kind of number that gets quoted in a meeting.
 */
export const FRAUD_SIGNAL_MIN_SAMPLE = 20;
/**
 * The fewest visits a region or a salesperson needs before a RATE is printed.
 *
 * Same discipline as `FRAUD_SIGNAL_MIN_SAMPLE`, applied to the other
 * denominator in this module. Three visits of which three flagged is not a 100%
 * crisis, and a percentage rendered from it outlives every memory of how it was
 * computed — it gets carried into a meeting as "that region is at a hundred
 * percent". Below this the counts are still shown and the rate reads as "—".
 */
export const FRAUD_RATE_MIN_VISITS = 30;
/**
 * One row of the FR-16 drill-down — a region at level 1, a salesperson at
 * level 2. ONE SHAPE for both, so the table and the chart are written once.
 *
 * THE RATE IS THE COMPARABLE NUMBER AND THE COUNTS ARE THE HONEST ONE. Ranking
 * regions by absolute flags just ranks them by size; ranking by rate alone lets
 * a three-visit region top the list. Both are on screen, always.
 *
 * THE DENOMINATOR INCLUDES REJECTED VISITS, unlike every other aggregate in the
 * product. Everywhere else a rejected visit is one that did not happen and is
 * excluded; here it is precisely what is being counted, and excluding it would
 * make a region's numbers FALL as its supervisor confirmed fraud — the report
 * would show a problem vanishing exactly as it was proven.
 *
 * THE DISPOSITION BREAKDOWN IS NOT DECORATION. `flaggedVisits` counts every flag
 * raised, whatever a supervisor later decided, so the rate is partly a function
 * of detector noise rather than behaviour. A region at 200 flags and 2
 * rejections has a detector problem; one at 20 and 15 has a people problem. The
 * rate alone cannot tell them apart, so the four counts travel with it.
 */
export const FraudSummaryRow = z.object({
    /** Region id at level 1, sales user id at level 2. */
    id: Id,
    name: z.string(),
    /** Every visit in the window and scope — rejected ones included. See above. */
    visits: z.number().int(),
    /**
     * Visits carrying a flag. There is exactly one flag per visit
     * (`fraud_flags.visit_id` is UNIQUE), so this is also the flag count — carried
     * once rather than as two columns that could never disagree.
     */
    flaggedVisits: z.number().int(),
    /** Flagged visits per 100 visits. `null` below `FRAUD_RATE_MIN_VISITS`. */
    ratePer100: z.number().nullable(),
    pending: z.number().int(),
    followUp: z.number().int(),
    approved: z.number().int(),
    rejected: z.number().int(),
});
/**
 * How well one signal is doing — FR-16 threshold tuning (OI-10 / DAT-04).
 *
 * THE DENOMINATOR IS RESOLVED FLAGS, NOT ALL FLAGS. A flag still PENDING or in
 * FOLLOW_UP has no verdict yet, and counting it as "not a false positive" would
 * make every signal look accurate on the day it shipped and get worse as
 * supervisors caught up. `resolved` is APPROVED + REJECTED for the same reason
 * FOLLOW_UP is an open state everywhere else in this module.
 *
 * THE `solo` COLUMNS EXIST BECAUSE ATTRIBUTION IS AMBIGUOUS. A flag carrying
 * both `GPS_OUT_OF_TOLERANCE` and `MISSING_EXIF_METADATA` and dispositioned
 * "tolak" attributes that rejection to BOTH codes, even though the GPS evidence
 * may have been the entire reason. Notes are free text, so nothing can say
 * which one convinced the supervisor. Restricting to flags where a code fired
 * ALONE gives an unconfounded reading; the all-flags figure gives volume. Tune
 * on the solo one.
 */
export const FraudSignalQuality = z.object({
    code: FraudSignalCode,
    total: z.number().int(),
    pending: z.number().int(),
    followUp: z.number().int(),
    approved: z.number().int(),
    rejected: z.number().int(),
    /** APPROVED + REJECTED — the flags that actually carry a verdict. */
    resolved: z.number().int(),
    soloResolved: z.number().int(),
    soloApproved: z.number().int(),
    /**
     * The noise rate: of the flags this signal got a verdict on, how many were
     * "wajar". `null` below `FRAUD_SIGNAL_MIN_SAMPLE` resolved flags.
     */
    approvedPct: z.number().nullable(),
    /** The hit rate. `null` on the same rule. */
    rejectedPct: z.number().nullable(),
    /** The noise rate over flags where this signal fired alone — the one to tune on. */
    soloApprovedPct: z.number().nullable(),
});
/**
 * Setting a disposition requires the `fraud:disposition` permission.
 * TSP 7.5 C4 asserts a Sales token attempting this receives 403.
 */
export const SetDispositionRequest = z.object({
    flagId: Id,
    disposition: z.enum(['APPROVED', 'FOLLOW_UP', 'REJECTED']),
    note: z.string().max(2000).nullable(),
    /**
     * Only meaningful with `FOLLOW_UP`. Cleared by the server on a resolution,
     * because a due date surviving on a closed case is a date nobody honours.
     */
    dueAt: Timestamp.nullable().optional(),
    /**
     * "I spoke to the salesperson about this." Meaningful on FOLLOW_UP and
     * REJECTED, which are the two outcomes that reach an evaluation.
     *
     * NOT MANDATORY, deliberately. A required tick is one everybody makes without
     * reading, and the column would then record nothing. Leaving it false is a
     * real answer — unreachable, or no conversation needed — and the queue can
     * filter for it, which is what makes the field worth having at all.
     */
    staffContacted: z.boolean().optional(),
});
/**
 * The most flags one bulk clear may touch.
 *
 * A cap rather than a silent partial apply: clearing 200 of 340 and reporting
 * success is how a supervisor comes to believe a queue is empty when it is not.
 * Over the cap the request is REFUSED and the filter has to be narrowed, which
 * is also the moment somebody notices the shape of what they are clearing.
 */
export const FRAUD_BULK_CAP = 200;
/**
 * Clearing one root cause across many flags — FR-16.
 *
 * WHY THIS IS DANGEROUS AND HOW IT IS FENCED. Bulk disposition is how a review
 * process becomes a rubber stamp; the console already refuses one-click
 * dispositions beside truncated evidence for that reason. It earns its place
 * only for the case it was built for: ONE outlet whose master coordinate is
 * wrong, raising `GPS_OUT_OF_TOLERANCE` on every visit anybody ever makes to it.
 * Working forty identical cards one at a time teaches a supervisor to stop
 * reading, which costs more than the bulk action does.
 *
 * So it is fenced four ways, and each fence is load-bearing:
 *
 *   - ONE OUTLET AND ONE SIGNAL CODE. Both required. This cannot express "clear
 *     everything I can see", only "clear this specific recurring cause".
 *   - "WAJAR" ONLY. `REJECTED` removes visits from every number in the product
 *     and reaches an HR conversation; that decision is made one visit at a time
 *     or not at all. `FOLLOW_UP` in bulk would assign work nobody agreed to.
 *   - A MANDATORY SHARED NOTE, which is the actual root cause in words. It is
 *     written to every flag it touches, so each one remains individually
 *     reviewable afterwards.
 *   - A HARD CAP, refusing rather than truncating.
 */
export const BulkDispositionRequest = z.object({
    outletId: Id,
    signalCode: FraudSignalCode,
    /** Wajar only. See above — this is not the place to reject anything. */
    disposition: z.literal('APPROVED'),
    note: z.string().min(1).max(2000),
});
/**
 * Undo — the safety valve that `setDisposition` deliberately does not provide.
 *
 * A SEPARATE REQUEST rather than widening `SetDispositionRequest.disposition`
 * to include `PENDING`. That enum states truthfully that a disposition is a
 * decision, and reopening is not one: it is the admission that the decision
 * should not have been made. The note is MANDATORY here — a reversal nobody
 * explained is exactly the entry an HR review will stop on.
 */
export const ReopenFlagRequest = z.object({
    flagId: Id,
    note: z.string().min(1).max(2000),
});
/**
 * One entry in a flag's timeline, read from the append-only audit trail.
 *
 * `actorName` is nullable because `audit_events.actor_id` carries no foreign
 * key and the deletion flow anonymises users: a real entry can outlive the
 * account that made it, and the trail is right to keep it.
 */
export const FraudFlagHistoryEntry = z.object({
    type: z.string(),
    occurredAt: Timestamp,
    actorId: Id.nullable(),
    actorName: z.string().nullable(),
    actorRole: z.string().nullable(),
    disposition: FlagDisposition.nullable(),
    note: z.string().nullable(),
});
//# sourceMappingURL=fraud.js.map